import React from 'react';

import { H2, H4, H5 } from 'primitives/appTitle';
import AppSection from 'primitives/appSection';
import { AppInputTextFormik } from 'primitives/input/appInputText';

import {
  AddressValues,
  FindZoneByCoordinatesPayload,
  PhotographerApplicationDTO,
  Zone,
} from 'appAPITypes';
import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import { CountryOptionsForPhotographer } from 'appConstants';
import { AppInputSelectFormik } from 'primitives/input/appInputSelect';
import { get } from 'lodash';

import PhotographerMap from './components/photographerMap';
import PhotographerZoneRows from './components/photographerZoneRows';
import ZonesAutocompleteInput from './components/zonesAutocompleteInput';
import { getFormikInputBindProps } from 'primitives/input/formikUtils';
import { findZoneByCoordinates } from 'appAPI';
import { KeyAndLabel } from 'appTypes';
import { ShText } from '@shoootin/translations';
import { JoinPageTranslations } from '../../joinPageTranslations';
import { getFrontMessage } from '../../../../appMessageDefinitions';
import { getPlaceDetails } from '../../../../primitives/maps/appMapHooks';
import AppGooglePlaceAutocompleteInput from '../../../../primitives/maps/appGooglePlaceAutocompleteInput';
import { useGoogleMapsLoaded } from '../../../../primitives/maps/appMapLoader';
import { ShCountry, ShCountryConfigs } from '@shoootin/config';
import { ShCol, ShRow, ShSpinner } from '@shoootin/components-web';

const fetchZoneByCoordinates = async (
  payload: FindZoneByCoordinatesPayload,
): Promise<Zone> => {
  console.log('findZoneByCoordinates', payload);

  const zone = await findZoneByCoordinates(payload);
  console.debug('zones fetched', { zone });
  return zone;
};

const isAdressSelected = (address: AddressValues): boolean =>
  !!address &&
  !!address.country &&
  !!address.address &&
  !!address.longitude &&
  !!address.latitude &&
  !!address.city &&
  !!address.cityId;

export const Localisation = ({
  form,
}: {
  form: FormikProps<PhotographerApplicationDTO>;
}) => {
  const mapsLoaded = useGoogleMapsLoaded();

  const zonesArrayName = 'zones';
  const zones: Zone[] = get(form.values, zonesArrayName);
  const country = get(form.values, 'address.country');
  const handlePlaceSelected = async (
    place: google.maps.places.PlaceResult,
    zoneArray: FieldArrayRenderProps,
  ) => {
    console.debug(place);
    const location = place!.geometry!.location;
    console.log(place, location.lat(), location.lng(), place.formatted_address);
    //@ts-ignore
    form.setFieldValue('address.latitude', location.lat());
    //@ts-ignore
    form.setFieldValue('address.longitude', location.lng());
    //@ts-ignore
    form.setFieldValue('address.address', place.formatted_address);

    const zone = await fetchZoneByCoordinates({
      latitude: location.lat(),
      longitude: location.lng(),
      country: country,
    });
    console.log('handlePlaceSelected fetchZoneByCoordinates', zone);

    //@ts-ignore
    form.setFieldValue('address.cityId', zone.id);
    //@ts-ignore
    form.setFieldValue('address.zipcode', zone.zipcode);

    //TODO we need to handle area where we don't have any mapped zones.
    //Backend will return City with id = -1
    if (zone.id === -1) {
      //TODO we must switch to some state where photographer cannot chose his zone
      //and what happens if user already choose some zones ?

      console.warn(
        'This coordinates does not have any mapped area so we cannot draw polygons on map',
      );
      //TODO handle city name from place
      console.log(
        'address_components',
        place.address_components!.filter((each) =>
          each.types.includes('locality'),
        ),
      );
      //@ts-ignore
      form.setFieldValue(
        //@ts-ignore
        'address.city',
        place.address_components!.filter((each) =>
          each.types.includes('locality'),
        )[0].long_name,
      );
    } else {
      //@ts-ignore
      form.setFieldValue('address.city', zone.name);
      const isZoneAlreadyAdded = !!form.values.zones.find(function (o) {
        return o.id == zone.id;
      });
      if (!isZoneAlreadyAdded) {
        // TODO, temp workaround for https://github.com/jaredpalmer/formik/issues/1685
        form.setFieldValue(zonesArrayName, [zone]);
      }
    }
  };

  const handleMapClick = (
    form: FormikProps<PhotographerApplicationDTO>,
    event: google.maps.MouseEvent,
    zoneArray: FieldArrayRenderProps,
  ) => {
    if (isAdressSelected(form.values.address)) {
      fetchZoneByCoordinates({
        country: get(form.values, 'country'),
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
      }).then((zone) => {
        if (zone.id === -1) {
          //TODO this zone is not mapped yet.
        } else {
          zoneArray.push(zone);
        }
      });
    } else {
      //@ts-ignore
      form.setFieldError('address.address', 'Please input your address first');
      //@ts-ignore
      form.setFieldTouched('address.address');
    }
  };

  const handleCountryChange = (option: KeyAndLabel<ShCountry>) => {
    if (form.values.address.country !== option.key) {
      console.log('Country has changed', option);
      const newCoords = ShCountryConfigs[option.key].centerLatLng;
      //@ts-ignore
      form.setFieldValue('address.address', '');
      //@ts-ignore
      form.setFieldValue('address.city', '');
      //@ts-ignore
      form.setFieldValue('address.zipcode', '');
      //@ts-ignore
      form.setFieldValue('address.cityId', undefined);
      form.setFieldValue('zones', []);
      //@ts-ignore
      form.setFieldValue('address.latitude', newCoords.latitude);
      //@ts-ignore
      form.setFieldValue('address.longitude', newCoords.longitude);
    }
  };

  const cityId = get(form.values, 'address.cityId');
  const addressIsInMappedZone = cityId && cityId !== -1;

  // TODO migrate to useFieldArray once available (https://github.com/jaredpalmer/formik/issues/1476)
  // TODO the provider is currently mandatory due to using hooks (https://github.com/jaredpalmer/formik/issues/1477)
  return (
    <FieldArray name={zonesArrayName}>
      {(zoneArray) => (
        <>
          {!mapsLoaded && <ShSpinner />}
          {mapsLoaded && (
            <AppSection
              className="photograph-location left"
              header={
                <H2>
                  <ShText message={JoinPageTranslations.localisationTitle} />
                </H2>
              }
            >
              <ShRow
                className="field-row wrap-s"
                css={{ marginBottom: '2.5rem' }}
              >
                <ShCol xs={60} md={15}>
                  <AppInputSelectFormik
                    form={form}
                    name="address.country"
                    placeholderMessage={getFrontMessage(
                      'form_address_country_PlaceHolder',
                    )}
                    options={CountryOptionsForPhotographer}
                    onOptionSelected={(option) => {
                      handleCountryChange(option);
                    }}
                  />
                </ShCol>

                <ShCol xs={60} md={30}>
                  <AppGooglePlaceAutocompleteInput
                    {...getFormikInputBindProps(form, 'address.address')}
                    onChange={(text) =>
                      // @ts-ignore
                      form.setFieldValue('address.address', text)
                    }
                    // hack to disable chrome autocomplete...
                    autoComplete="new-password"
                    placeholderMessage={getFrontMessage(
                      'form_address_address_PlaceHolder',
                    )}
                    modifier="large"
                    required
                    country={form.values.address.country}
                    onPlaceSelected={async (placeResult) => {
                      const place = await getPlaceDetails(placeResult.place_id);
                      await handlePlaceSelected(place, zoneArray);
                    }}
                  />
                </ShCol>
                <ShCol xs={60} md={15}>
                  <AppInputTextFormik
                    form={form}
                    name="address.city"
                    placeholderMessage={getFrontMessage(
                      'form_address_city_PlaceHolder',
                    )}
                    modifier="large"
                    readOnly
                  />
                </ShCol>
              </ShRow>

              {addressIsInMappedZone && (
                <ShRow className="photograph-text">
                  <ShCol xs={60} md={30}>
                    <H4>
                      <ShText
                        message={JoinPageTranslations.localisationSubtitle}
                      />
                    </H4>
                    <ShText
                      message={JoinPageTranslations.localisationSubtitle1}
                      tagName="p"
                    />
                    <ShText
                      message={JoinPageTranslations.localisationSubtitle2}
                      tagName="p"
                    />
                  </ShCol>
                </ShRow>
              )}
              {addressIsInMappedZone && (
                <H5>
                  <ShText message={JoinPageTranslations.localisationSubTitle} />
                </H5>
              )}
              <ShRow className="photograph-zones">
                <ShCol
                  xs={60}
                  md={
                    addressIsInMappedZone &&
                    isAdressSelected(form.values.address)
                      ? 40
                      : 60
                  }
                  className={'photograph-zones__map'}
                >
                  <PhotographerMap
                    zones={zones}
                    onMapClick={(event) => {
                      addressIsInMappedZone &&
                        handleMapClick(form, event, zoneArray);
                    }}
                    onZonePolygonClick={(zone, index) => {
                      zoneArray.remove(index);
                    }}
                    address={form.values.address}
                  />
                </ShCol>
                {addressIsInMappedZone &&
                  isAdressSelected(form.values.address) && (
                    <ShCol
                      xs={60}
                      md={{ span: 19, offset: 1 }}
                      className="photograph-zones__legend"
                    >
                      <ZonesAutocompleteInput
                        onZoneSelected={(zone) => zoneArray.push(zone)}
                        form={form}
                      />
                      {zones.length > 0 && (
                        <ul className="photograph-zones__labels">
                          <PhotographerZoneRows
                            zones={zones}
                            onZoneRemoveClick={(zone, index) =>
                              zoneArray.remove(index)
                            }
                          />
                        </ul>
                      )}
                    </ShCol>
                  )}
              </ShRow>
            </AppSection>
          )}
        </>
      )}
    </FieldArray>
  );
};
